import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Cookie } from 'lucide-react';
import { Link } from 'react-router-dom';

const COOKIE_CONSENT_KEY = 'geile-kiste-cookie-consent';

export const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    setIsVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'declined');
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
          data-testid="cookie-banner"
        >
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
            <div className="p-6 md:p-8">
              <div className="flex items-start gap-4">
                {/* Cookie Icon */}
                <div className="hidden sm:flex w-12 h-12 rounded-xl bg-[#3ECDC3]/10 items-center justify-center flex-shrink-0">
                  <Cookie className="w-6 h-6 text-[#3ECDC3]" />
                </div>
                
                {/* Content */}
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-2" >
                    Cookie-Einstellungen
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    Wir verwenden auf dieser Website ausschließlich technisch notwendige Cookies, 
                    die für den Betrieb der Seite erforderlich sind. Diese Cookies speichern keine 
                    persönlichen Daten und dienen nicht zu Tracking-Zwecken. Für das Kontaktformular 
                    nutzen wir Google reCAPTCHA zum Schutz vor Spam.
                  </p>
                  <p className="text-gray-500 text-xs mb-4">
                    Mehr Informationen finden Sie in unserer{' '}
                    <Link to="/datenschutz" className="text-[#3ECDC3] hover:underline">
                      Datenschutzerklärung
                    </Link>
                    .
                  </p>
                  
                  {/* Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleAccept}
                      className="px-6 py-3 bg-[#3ECDC3] text-white font-semibold rounded-xl hover:bg-[#2BB8AF] transition-colors shadow-md hover:shadow-lg"
                      data-testid="cookie-accept-btn"
                    >
                      Akzeptieren
                    </button>
                    <button
                      onClick={handleDecline}
                      className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:border-gray-400 hover:bg-gray-50 transition-colors"
                      data-testid="cookie-decline-btn"
                    >
                      Nur notwendige
                    </button>
                  </div>
                </div>
                
                {/* Close Button */}
                <button
                  onClick={handleDecline}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                  aria-label="Schließen"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
